import React, { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StaffImport } from "@/components/StaffImport";
import { fmtDateTime, titleCase, StatusBadge } from "@/lib/format";
import { PageHeader, DataTable } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { UserCog, Plus, Edit, ShieldOff, Award } from "lucide-react";
import { SalesListDialog } from "@/pages/EmployeeSales";
import { SetUserPassword } from "@/components/PasswordForms";
import { toast } from "sonner";

export default function Users() {
  const { user: me } = useAuth();
  const isAdmin = ["super_admin", "admin"].includes(me?.role);
  const [salesOf, setSalesOf] = React.useState(null);
  const canSetPw = (t) => {
    if (!me || t.id === me.id || t.role === "super_admin") return false;
    if (isAdmin) return true;
    if (me.role === "sales_manager") return !["admin", "sales_manager"].includes(t.role);
    if (me.role === "team_leader") return t.team_leader_id === me.id;
    return false;
  };
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/users").then(({ data }) => setUsers(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/roles").then(({ data }) => setRoles(data)).catch(() => {}); }, []);

  const shown = me?.role === "team_leader" ? users.filter((u) => u.id === me.id || u.team_leader_id === me.id) : users;
  const roleOpts = roles.map((r) => ({ value: r.value, label: r.label }));
  const leaders = users.filter((u) => ["team_leader", "sales_manager"].includes(u.role));

  const create = async (v) => { await api.post("/users", v); load(); };
  const edit = async (u, v) => { await api.put(`/users/${u.id}`, v); load(); };
  const resetMfa = async (u) => {
    if (!window.confirm(`Reset 2-step login for ${u.name}? They will scan a new QR code at their next sign-in.`)) return;
    try { await api.post(`/auth/mfa/reset/${u.id}`); toast.success("2-step login reset"); load(); } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
  };

  const createFields = [
    { name: "name", label: "Full Name", required: true },
    { name: "employee_id", label: "Employee ID", required: true, placeholder: "OMI-EMP-901" },
    { name: "role", label: "Role", type: "select", required: true, options: roleOpts },
    { name: "department", label: "Business", type: "select", options: [{ value: "insurance", label: "Insurance" }, { value: "loans", label: "Loans" }, { value: "both", label: "Both" }] },
    { name: "password", label: "Password", required: true, type: "password" },
    { name: "email", label: "Email", type: "email" },
    { name: "phone", label: "Phone" },
    { name: "team_leader_id", label: "Reports To (Team Leader)", type: "select", options: leaders.map((l) => ({ value: l.id, label: l.name })) },
  ];
  const editFields = [
    { name: "name", label: "Full Name" },
    { name: "role", label: "Role", type: "select", options: roleOpts },
    { name: "department", label: "Business", type: "select", options: [{ value: "insurance", label: "Insurance" }, { value: "loans", label: "Loans" }, { value: "both", label: "Both" }] },
    { name: "phone", label: "Phone" },
    { name: "active", label: "Active", type: "select", options: [{ value: "true", label: "Active" }, { value: "false", label: "Inactive" }] },
    { name: "password", label: "Reset Password", type: "password" },
  ];
  const columns = [
    { key: "employee_id", header: "Employee ID", render: (r) => <span className="font-mono text-xs font-semibold">{r.employee_id}</span> },
    { key: "name", header: "Name", render: (r) => <span className="font-semibold">{r.name}</span> },
    { key: "role", header: "Role", render: (r) => <span className={`text-sm ${r.role === "super_admin" ? "font-semibold text-primary" : ""}`}>{titleCase(r.role)}</span> },
    { key: "email", header: "Email", render: (r) => <span className="text-sm text-muted-foreground">{r.email || "—"}</span> },
    { key: "active", header: "Status", render: (r) => <span className={`text-xs font-semibold ${r.active ? "text-emerald-700" : "text-muted-foreground"}`}>{r.active ? "Active" : "Inactive"}</span> },
    { key: "actions", header: "", render: (r) => (
      <div className="flex" onClick={(e) => e.stopPropagation()}>
        <Button size="icon" variant="ghost" className="h-8 w-8" title="Sales" onClick={() => setSalesOf(r)} data-testid="user-sales-btn"><Award className="h-4 w-4" /></Button>
        {canSetPw(r) && <SetUserPassword target={r} />}
        {isAdmin && !(r.role === "super_admin" && me.role !== "super_admin") && <FormDialog title={`Edit ${r.name}`} fields={r.id === me.id ? editFields.filter((f) => !["role", "active", "password"].includes(f.name)) : editFields} initial={{ name: r.name, role: r.role, department: r.department || "both", phone: r.phone, active: String(r.active) }} onSubmit={(v) => edit(r, v)} submitLabel="Update"
          trigger={<Button size="icon" variant="ghost" className="h-8 w-8" data-testid="user-edit-btn"><Edit className="h-4 w-4" /></Button>} />}
        {me?.role === "super_admin" && r.id !== me.id && r.totp_enabled && <Button size="icon" variant="ghost" className="h-8 w-8 text-amber-600" title="Reset 2-step login (new QR at next sign-in)" onClick={() => resetMfa(r)} data-testid="user-mfa-reset-btn"><ShieldOff className="h-4 w-4" /></Button>}
      </div>
    ) },
  ];
  return (
    <div>
      <PageHeader title="Users & Roles" subtitle={`${shown.length} employees · role-based access control`} icon={UserCog}
        actions={isAdmin && <div className="flex gap-2"><StaffImport onDone={load} /><FormDialog title="Add Employee" fields={createFields} onSubmit={create} submitLabel="Create User" testId="create-user-dialog"
          trigger={<Button data-testid="create-user-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Employee</Button>} /></div>} />
      <DataTable columns={columns} rows={shown} loading={loading} rowTestId="user-table-row" />
      <SalesListDialog user={salesOf} onClose={() => setSalesOf(null)} />
    </div>
  );
}
